import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Table from 'components/application/Table';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import CourseTraining from 'components/training/CourseTraining';

import { sortAlphabetically, sortByDate } from 'components/helpers/arrays';
import { mappedAutoEnrolmentExclusionsByConfigurationId } from 'components/helpers/resources/autoEnrolments';
import { getELearningCourseId } from 'components/helpers/resources/courses';
import { groupTrainingByCourseId } from 'components/helpers/resources/training';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function RoleTrainingTable({
  attachments,
  autoEnrolmentConfigurations,
  autoEnrolmentExclusions,
  bookers,
  coursesWithTrainingStatusInfo,
  creditInfoUnavailable,
  currentBookings,
  currentPersonnel,
  currentRegistrations,
  eLearningAllowance,
  eLearningAllowed,
  eLearningCourses,
  hasTrainingEditableAccess,
  onAddBooking,
  onAddELearningBooking,
  onBookingReminderClick,
  onCreateAutoEnrolmentExclusion,
  onDeleteAutoEnrolmentExclusion,
  onDeleteTrainingClick,
  onEditTrainingClick,
  onNewTrainingClick,
  onRemoveBooking,
  onShowBooking,
  onUserCourseCreate,
  onUserCourseUpdate,
  setModalBoxData,
  training,
}) {
  const currentActor = useCurrentActor();

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const trainingByCourseId = groupTrainingByCourseId({
    training,
  });

  const orderedRows = coursesWithTrainingStatusInfo.sort((a, b) => {
    const sortByOrderingBand = (a, b) => a.orderingBand - b.orderingBand;
    const orderingSort = sortByOrderingBand(a, b);
    if (orderingSort !== 0) {
      return orderingSort;
    }
    const dateSort = sortByDate(
      a.latestTraining && a.latestTraining.attributes.expiryDate,
      b.latestTraining && b.latestTraining.attributes.expiryDate,
    );
    if (dateSort !== 0) {
      return dateSort;
    }
    return sortAlphabetically(
      a.course.attributes.name,
      b.course.attributes.name,
    );
  });

  return (
    <Table
      headers={
        <tr>
          <th className='tw-w-16.5 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900' />
          <th className='tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
            {i18nTShared('name')}
          </th>
          <th className='tw-w-31 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
            {i18nTShared('expiryDate')}
          </th>
          <th className='tw-w-54 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
            {i18nTShared('bookings')}
          </th>
          {currentActor.user.attributes.accessType !== 'personnel' && (
            <th className='fw-72 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
              {i18nTShared('actions')}
            </th>
          )}
        </tr>
      }
      rows={orderedRows.map((courseData) => {
        const {
          course,
          isRequired,
          isRequiredByCourseCompanyRole,
          isRequiredByUserCourse,
          personnelCourseConnectionRequirementPriority,
          personnelCourseConnectionRequirementPriorityUser,
          requiredSource,
          trainingStatus,
          userCourse,
        } = courseData;

        const eLearningCourse = eLearningCourses[getELearningCourseId(course)];
        const autoEnrolmentConfiguration =
          autoEnrolmentConfigurations &&
          autoEnrolmentConfigurations[
            course.relationships?.autoEnrolmentConfiguration?.data?.id
          ];
        const autoEnrolmentExclusion =
          autoEnrolmentExclusions &&
          mappedAutoEnrolmentExclusionsByConfigurationId(
            autoEnrolmentExclusions,
          )[autoEnrolmentConfiguration?.id];

        const booking = currentBookings.find(
          (booking) => booking.relationships.course.data.id === course.id,
        );
        const booker =
          bookers && bookers[booking?.relationships?.booker?.data?.id];
        const registration = currentRegistrations.find(
          (registration) =>
            registration.relationships.course.data.id === course.id,
        );

        return (
          <CourseTraining
            attachments={attachments}
            autoEnrolmentConfiguration={autoEnrolmentConfiguration}
            autoEnrolmentExclusion={autoEnrolmentExclusion}
            booker={booker}
            booking={booking}
            course={course}
            creditInfoUnavailable={creditInfoUnavailable}
            currentPersonnel={currentPersonnel}
            eLearningAllowance={eLearningAllowance}
            eLearningAllowed={eLearningAllowed}
            eLearningCourse={eLearningCourse}
            hasTrainingEditableAccess={hasTrainingEditableAccess}
            isRequired={isRequired}
            isRequiredByCourseCompanyRole={isRequiredByCourseCompanyRole}
            isRequiredByUserCourse={isRequiredByUserCourse}
            key={`roleCourseTraining--${course.id}`}
            onAddBooking={onAddBooking}
            onAddELearningBooking={onAddELearningBooking}
            onBookingReminderClick={onBookingReminderClick}
            onCreateAutoEnrolmentExclusion={onCreateAutoEnrolmentExclusion}
            onDeleteAutoEnrolmentExclusion={onDeleteAutoEnrolmentExclusion}
            onDeleteTrainingClick={onDeleteTrainingClick}
            onEditTrainingClick={onEditTrainingClick}
            onNewTrainingClick={onNewTrainingClick}
            onRemoveBooking={onRemoveBooking}
            onShowBooking={onShowBooking}
            onUserCourseCreate={onUserCourseCreate}
            onUserCourseUpdate={onUserCourseUpdate}
            personnelCourseConnectionRequirementPriority={
              personnelCourseConnectionRequirementPriority
            }
            personnelCourseConnectionRequirementPriorityUser={
              personnelCourseConnectionRequirementPriorityUser
            }
            registration={registration}
            requiredSource={requiredSource}
            setModalBoxData={setModalBoxData}
            training={trainingByCourseId[course.id]}
            trainingStatus={trainingStatus}
            userCourse={userCourse}
          />
        );
      })}
    />
  );
}

RoleTrainingTable.propTypes = {
  attachments: PropTypes.array.isRequired,
  autoEnrolmentConfigurations: PropTypes.object,
  autoEnrolmentExclusions: PropTypes.object,
  bookers: PropTypes.objectOf(resourceShape('simpleUser')),
  creditInfoUnavailable: PropTypes.bool,
  coursesWithTrainingStatusInfo: PropTypes.arrayOf(
    PropTypes.shape({
      course: resourceShape('course'),
      isRequired: PropTypes.bool,
      isAssignedToAnotherRole: PropTypes.bool,
      latestTraining: resourceShape('training'),
      orderingBand: PropTypes.number,
      requiredSource: PropTypes.string,
      trainingStatus: PropTypes.string,
      userCourse: resourceShape('userCourse'),
    }),
  ),
  currentBookings: PropTypes.array,
  currentPersonnel: PropTypes.object,
  currentRegistrations: PropTypes.array,
  eLearningAllowance: PropTypes.object,
  eLearningAllowed: PropTypes.bool,
  eLearningCourses: PropTypes.object,
  hasTrainingEditableAccess: PropTypes.bool,
  onAddBooking: PropTypes.func,
  onAddELearningBooking: PropTypes.func,
  onBookingReminderClick: PropTypes.func.isRequired,
  onCreateAutoEnrolmentExclusion: PropTypes.func,
  onUserCourseCreate: PropTypes.func,
  onDeleteAutoEnrolmentExclusion: PropTypes.func,
  onDeleteTrainingClick: PropTypes.func,
  onEditTrainingClick: PropTypes.func,
  onNewTrainingClick: PropTypes.func,
  onRemoveBooking: PropTypes.func.isRequired,
  onShowBooking: PropTypes.func.isRequired,
  onUserCourseUpdate: PropTypes.func,
  setModalBoxData: PropTypes.func,
  training: PropTypes.array.isRequired,
};
